import React, { useState, useEffect } from 'react';
import * as d3 from 'd3';
import './ListVisualizer.css'; // Import the CSS file for styling

const LinkedListVisualizer = () => {
  const [listType, setListType] = useState(''); // Modes: singly, circular, doubly
  const [linkedList, setLinkedList] = useState([]);
  const [inputValue, setInputValue] = useState('');

  // Reset linked list when list type changes
  useEffect(() => {
    setLinkedList([]); // Clear the linked list
  }, [listType]);

  // Function to validate input
  const isValidInput = (value) => {
    const number = parseInt(value);
    return !isNaN(number) && value.trim() !== '';
  };

  // Singly Linked List Operations
  const singlyInsert = () => {
    if (isValidInput(inputValue)) {
      setLinkedList([...linkedList, parseInt(inputValue)]);
      setInputValue('');
    }
  };

  const singlyDelete = () => {
    if (isValidInput(inputValue)) {
      const index = linkedList.indexOf(parseInt(inputValue));
      if (index > -1) {
        const newList = [...linkedList];
        newList.splice(index, 1);
        setLinkedList(newList);
      } else {
        alert('Value not found in the list.');
      }
      setInputValue('');
    }
  };

  const singlyInsertEnd = () => {
    if (isValidInput(inputValue)) {
      setLinkedList([parseInt(inputValue), ...linkedList.reverse()]);
      setInputValue('');
    }
  };

  const singlyInsertOrdered = () => {
    if (isValidInput(inputValue)) {
      const value = parseInt(inputValue);
      setLinkedList([...linkedList, value].sort((a, b) => a - b));
      setInputValue('');
    }
  };

  const singlyCountNodes = () => {
    alert(`Total nodes in Singly Linked List: ${linkedList.length}`);
  };

  const singlyCopyList = () => {
    const copiedList = [...linkedList];
    alert(`Copied List: ${copiedList}`);
  };

  // Circular Linked List Operations
  const circularInsertFirst = () => {
    if (isValidInput(inputValue)) {
      setLinkedList([parseInt(inputValue), ...linkedList]);
      setInputValue('');
    }
  };

  const circularInsertLast = () => {
    if (isValidInput(inputValue)) {
      setLinkedList([...linkedList, parseInt(inputValue)]);
      setInputValue('');
    }
  };

  const circularInsertOrdered = () => {
    if (isValidInput(inputValue)) {
      const value = parseInt(inputValue);
      setLinkedList([...linkedList, value].sort((a, b) => a - b));
      setInputValue('');
    }
  };

  const circularDelete = () => {
    if (isValidInput(inputValue)) {
      const index = linkedList.indexOf(parseInt(inputValue));
      if (index > -1) {
        const newList = [...linkedList];
        newList.splice(index, 1);
        setLinkedList(newList);
      } else {
        alert('Value not found in the list.');
      }
      setInputValue('');
    }
  };

   // Circular Head Operations
   const circularHeadInsertFirst = () => {
     if (isValidInput(inputValue)) {
       setLinkedList([parseInt(inputValue), ...linkedList]);
       setInputValue('');
     }
   };

   const circularHeadInsertLast = () => {
     if (isValidInput(inputValue)) {
       setLinkedList([...linkedList, parseInt(inputValue)]);
       setInputValue('');
     }
   };

   const circularHeadInsertAfterP = () => {
     if (isValidInput(inputValue)) {
       const index = linkedList.indexOf(parseInt(inputValue));
       if (index > -1) {
         const newNodeValue = parseInt(prompt("Enter value to insert after:"));
         if (!isNaN(newNodeValue)) {
           const newList = [...linkedList];
           newList.splice(index + 1, 0, newNodeValue);
           setLinkedList(newList);
         }
       } else {
         alert('Value not found in the list.');
       }
       setInputValue('');
     }
   };

   // Doubly Linked List Operations
   const doublyInsert = () => {
     if (isValidInput(inputValue)) {
       setLinkedList([...linkedList, parseInt(inputValue)]);
       setInputValue('');
     }
   };

   const doublyDelete = () => {
     if (isValidInput(inputValue)) {
       const index = linkedList.indexOf(parseInt(inputValue));
       if (index > -1) {
         const newList = [...linkedList];
         newList.splice(index, 1);
         setLinkedList(newList);
       } else {
         alert('Value not found in the list.');
       }
       setInputValue('');
     }
   };

   // Visualization function
   const drawList = () => {
     const svg = d3.select('#linkedlist-container').attr('width', '100%').attr('height', '100');
     svg.selectAll('*').remove(); // Clear previous visualization

     // Draw nodes
     svg.selectAll('circle')
       .data(linkedList)
       .enter()
       .append('circle')
       .attr('cx', (d, i) => i * 80 + 50)
       .attr('cy', 50)
       .attr('r', 30)
       .attr('fill', 'skyblue')
       .attr('stroke', 'black')
       .attr('stroke-width', 2);

     // Draw data labels
     svg.selectAll('text')
       .data(linkedList)
       .enter()
       .append('text')
       .attr('x', (d, i) => i * 80 + 50)
       .attr('y', 55)
       .attr('text-anchor', 'middle')
       .attr('font-size', '16px')
       .attr('fill', 'black')
       .text(d => d);

     // Draw arrows for all Linked Lists (Singly, Circular, Doubly)
     svg.selectAll('line')
       .data(linkedList.slice(0, -1)) // All except the last node
       .enter()
       .append('line')
       .attr('x1', (d, i) => i * 80 + 80) // Starting point of each node
       .attr('y1', 50)
       .attr('x2', (d, i) => (i + 1) * 80 + 50 -30) // Ending point for next node at circle boundary
       .attr('y2', 50)
       .attr('stroke', 'black')
       .attr('stroke-width',2)
       .attr('marker-end','url(#arrow)');
       
     // For Circular Linked List: Curved line from the last node to the first node
     if (listType === 'circular' && linkedList.length >1){
        const lastNodeX=(linkedList.length-1)*80+80; // Position of the last node
        const firstNodeX=50; // Position of the first node
        const curveHeight=20; // Adjust this for height of curve

        svg.append("path")
          .attr("d",`M${lastNodeX},50 C${lastNodeX +30},${50-curveHeight} ${firstNodeX-30},${50-curveHeight} ${firstNodeX-30},50`)
          .attr("stroke","black")
          .attr("stroke-width",2)
          .attr("fill","none")
          .attr("marker-end","url(#arrow)");
     }
   };

   useEffect(() => { drawList(); }, [linkedList]);

   return (
     <div className="container">
       <h2>Linked List Visualizer</h2>
       
       <div className="svg-container">
         <svg id="linkedlist-container"></svg>
       </div>

       <div className="button-group">
         <button onClick={() => setListType('singly')}>Singly Linked List</button>
         <button onClick={() => setListType('circular')}>Circular Linked List</button>
         <button onClick={() => setListType('doubly')}>Doubly Linked List</button>
       </div>

       {listType && (
         <div className="operation-section">
           <input 
             type="number" 
             value={inputValue} 
             onChange={(e) => setInputValue(e.target.value)} 
             placeholder="Enter value" 
           />
           <div className="action-buttons">
             {listType === 'singly' && (
               <>
                 <button onClick={singlyInsert}>Insert</button>
                 <button onClick={singlyInsertEnd}>Insert End</button>
                 <button onClick={singlyInsertOrdered}>Insert Ordered</button>
                 <button onClick={singlyDelete}>Delete</button>
                 <button onClick={singlyCountNodes}>Count Nodes</button>
                 <button onClick={singlyCopyList}>Copy List</button>
               </>
             )}
             {listType === 'circular' && (
               <>
                 <button onClick={circularInsertFirst}>Insert First</button>
                 <button onClick={circularInsertLast}>Insert Last</button>
                 <button onClick={circularInsertOrdered}>Insert Ordered</button>
                 <button onClick={circularDelete}>Delete</button>
                 <button onClick={circularHeadInsertFirst}>Head Insert First</button>
                 <button onClick={circularHeadInsertLast}>Head Insert Last</button>
                 <button onClick={circularHeadInsertAfterP}>Head Insert After P</button>
               </>
             )}
             {listType === 'doubly' && (
               <>
                 <button onClick={doublyInsert}>Insert</button>
                 <button onClick={doublyDelete}>Delete</button>
               </>
             )}
           </div>
         </div>
       )}
     </div>
   );
};

export default LinkedListVisualizer;
